import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { usePatchSettings, useSettings } from "@/api/hooks/admin";
import { errorMessage } from "@/api/errors";
import { ErrorNotice } from "@/components/ErrorNotice";
import { PageHeader } from "@/components/PageHeader";
import { PageLoading } from "@/components/Spinner";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { usePageCrumbs } from "@/app/layouts/shellContext";

export function SettingsPage() {
  const { t } = useTranslation();
  usePageCrumbs([{ label: t("common.nav.admin") }, { label: t("admin.nav.settings") }]);
  const settings = useSettings();
  const patch = usePatchSettings();
  if (settings.isPending) return <PageLoading />;
  if (settings.isError) return <ErrorNotice error={settings.error} />;
  const on = settings.data.allow_self_signup;
  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <PageHeader title={t("admin.settings.title")} description={t("admin.settings.intro")} />
      <Card>
        <CardContent className="flex items-start justify-between gap-6 pt-6">
          <div className="flex flex-col gap-1">
            <span id="signup-label" className="font-heading font-semibold text-heading">
              {t("admin.settings.selfSignup")}
            </span>
            <span id="signup-help" className="text-sm text-fg-muted">
              {t("admin.settings.selfSignupHelp")}
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-labelledby="signup-label"
            aria-describedby="signup-help"
            disabled={patch.isPending}
            onClick={() =>
              patch.mutate(
                { allow_self_signup: !on },
                {
                  onSuccess: () => toast.success(t("admin.settings.saved")),
                  onError: (e) => toast.error(errorMessage(e, t("errors.generic"))),
                },
              )
            }
            className={cn(
              "relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 disabled:opacity-60",
              on ? "bg-secondary" : "bg-border-strong",
            )}
          >
            <span
              className={cn(
                "inline-block h-6 w-6 rounded-full bg-surface shadow transition-transform",
                on ? "translate-x-7" : "translate-x-1",
              )}
            />
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
